// Libraries
import React from 'react';

// Supermove
import {Loading, Styled, Space, Icon} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useQuery, useResponsive, useToast} from '@supermove/hooks';
import {colors} from '@supermove/styles';

// App
import Button from '@shared/design/components/Button';
import IconButton from '@shared/design/components/IconButton';
import Line from '@shared/design/components/Line';
import Text from '@shared/design/components/Text';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import PageLoadingIndicator from '@shared/modules/App/components/PageLoadingIndicator';
import StageProjectStatus from '@shared/modules/Lead/enums/StageProjectStatus';
import {ProjectStatusType} from '@shared/modules/Project/enums/ProjectStatus';
import StageForm from '@shared/modules/Project/forms/StageForm';
import useCreateStageMutation from '@shared/modules/Project/hooks/useCreateStageMutation';
import EditStageFields from '@shared/modules/Sales/components/EditStageFields';

const Container = Styled.View`
  flex: 1;
`;

const HeaderContainer = Styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
`;

const BodyContainer = Styled.View`
  padding: 16px;
  background-color: ${colors.gray.background};
  flex: 1;
`;

const FieldsContainer = Styled.View`
  max-width: 400px;
  `;

const FooterContainer = Styled.View`
  padding: 16px;
  flex-direction: row;
  justify-content: flex-end;
`;

const AddStagePageContent = ({
  projectStatus,
  handleClose,
}: {
  projectStatus?: ProjectStatusType;
  handleClose: () => void;
}) => {
  const responsive = useResponsive();
  const successToast = useToast({ToastComponent: SuccessToast});

  const {loading, data} = useQuery(AddStagePageContent.query, {
    fetchPolicy: 'cache-and-network',
  });

  const organizationId = data?.viewer?.viewingOrganization?.id;

  const stageForm = StageForm.new({
    organizationId,
    projectStatus: projectStatus || StageProjectStatus.LEAD,
  });

  const {form, submitting, handleSubmit} = useCreateStageMutation({
    stageForm,
    onSuccess: () => {
      successToast.handleToast({message: 'Sales stage created.'});
      handleClose();
    },
    onError: (errors) => console.log({errors}),
  });

  return (
    <Loading loading={loading} as={PageLoadingIndicator}>
      {() => (
        <Container>
          <HeaderContainer>
            <Text.Heading2>Sales Stage Details</Text.Heading2>
            <IconButton source={Icon.Xmark} onPress={handleClose} isSecondary />
          </HeaderContainer>
          <Line />
          <BodyContainer>
            <FieldsContainer>
              <Text.Heading2>Overview</Text.Heading2>
              <Space height={16} />
              <EditStageFields form={form} />
            </FieldsContainer>
          </BodyContainer>
          <Line />
          <FooterContainer>
            <Button
              text={'Save'}
              onPress={handleSubmit}
              isSubmitting={submitting}
              isWidthOfContainer={!responsive.desktop}
              style={responsive.desktop ? {minWidth: 120} : {flex: 1}}
              isResponsive
            />
          </FooterContainer>
        </Container>
      )}
    </Loading>
  );
};

AddStagePageContent.query = gql`
  query AddStagePageContent {
    ${gql.query}
    viewer {
      id
      viewingOrganization {
        id
      }
    }
  }
`;

export default AddStagePageContent;
